import ReactDOM from "react-dom";
import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, FileCheck, AlertCircle, CheckCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const QC_LEVELS = ["qc1_inovit", "qc2_gmr", "qc3_discom"];
const QC_COLORS = {
  qc1_inovit: "bg-teal-100 text-teal-700", qc2_gmr: "bg-cyan-100 text-cyan-700",
  qc3_discom: "bg-sky-100 text-sky-700"
};

export default function QCPage() {
  const { user } = useAuth();
  const [qcs, setQCs] = useState([]);
  const [reworks, setReworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("qc");
  const [levelFilter, setLevelFilter] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState("");
  const [importing, setImporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (levelFilter) params.qc_level = levelFilter;
      if (resultFilter) params.result = resultFilter;
      const [qcRes, reworkRes] = await Promise.all([
        api.get("/qc", { params }),
        api.get("/rework")
      ]);
      setQCs(qcRes.data.items);
      setReworks(reworkRes.data.items);
    } catch { toast.error("Failed to load QC data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [levelFilter, resultFilter]);

  const handleApproveRework = async (id) => {
    try {
      await api.put(`/rework/${id}/approve`);
      toast.success("Rework approved — financial lock released");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleBulkImport = async () => {
    setImporting(true);
    try {
      let records;
      try { records = JSON.parse(importData); }
      catch { toast.error("Invalid JSON format"); setImporting(false); return; }
      const res = await api.post("/qc/bulk-import", { records });
      toast.success(`Imported ${res.data.imported} QC records. ${res.data.errors?.length || 0} errors.`);
      setShowImport(false);
      setImportData("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const sampleJson = JSON.stringify([
    { dt_code: "DT-VAR-001", qc_level: "qc1_inovit", result: "passed", qc_date: "2025-01-15" },
    { dt_code: "DT-VAR-002", qc_level: "qc1_inovit", result: "rejected", reject_reason: "Meter seal broken", qc_date: "2025-01-15" }
  ], null, 2);

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="qc-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>QC / Rework Management</h1>
          <p className="text-slate-500 text-sm">{qcs.length} QC records · {reworks.length} rework items</p>
        </div>
        {["admin", "ci"].includes(user?.role) && (
          <button onClick={() => setShowImport(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600" data-testid="import-qc-btn">
            <FileCheck size={16} />Import QC Results
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-sm p-1 w-fit">
        {[{ id: "qc", label: "QC Results" }, { id: "rework", label: `Rework (${reworks.length})` }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-1.5 rounded-sm text-sm font-semibold transition-colors ${activeTab === tab.id ? "bg-white text-orange-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "qc" && (
        <>
          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap gap-3">
            <select className="field-select h-9 text-xs min-w-[160px]" value={levelFilter} onChange={e => setLevelFilter(e.target.value)} data-testid="qc-level-filter">
              <option value="">All QC Levels</option>
              {QC_LEVELS.map(l => <option key={l} value={l}>{l.replace("_", " ").toUpperCase()}</option>)}
            </select>
            <select className="field-select h-9 text-xs min-w-[140px]" value={resultFilter} onChange={e => setResultFilter(e.target.value)} data-testid="qc-result-filter">
              <option value="">All Results</option>
              <option value="passed">Passed</option>
              <option value="rejected">Rejected</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
            <table className="dt-table">
              <thead>
                <tr><th>DT ID</th><th>QC Level</th><th>Result</th><th>Reason</th><th>QC Date</th><th>Circle</th></tr>
              </thead>
              <tbody>
                {loading ? [...Array(5)].map((_, i) => <tr key={i}>{[...Array(6)].map((_, j) => <td key={j}><div className="h-4 bg-slate-100 rounded animate-pulse"></div></td>)}</tr>)
                  : qcs.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-slate-400"><FileCheck size={32} className="mx-auto mb-2" />No QC records found</td></tr>
                  ) : qcs.map(qc => (
                    <tr key={qc.id}>
                      <td><span className="font-mono text-xs font-semibold">{qc.dt_id?.slice(-8)}</span></td>
                      <td><span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${QC_COLORS[qc.qc_level] || "bg-slate-100"}`}>{qc.qc_level?.replace("_", " ")}</span></td>
                      <td>
                        <span className={`flex items-center gap-1 text-xs font-bold ${qc.result === "passed" ? "text-green-600" : qc.result === "rejected" ? "text-red-600" : "text-yellow-600"}`}>
                          {qc.result === "passed" ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                          {qc.result?.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-xs text-slate-500 max-w-[200px] truncate">{qc.reject_reason || "—"}</td>
                      <td className="text-xs text-slate-500">{qc.qc_date ? new Date(qc.qc_date).toLocaleDateString("en-IN") : "—"}</td>
                      <td className="text-xs text-slate-600">{qc.circle_name}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === "rework" && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
          <table className="dt-table">
            <thead>
              <tr><th>DT ID</th><th>Type</th><th>Reason</th><th>Status</th><th>Circle</th><th>Assigned</th>{["admin", "ci", "supervisor"].includes(user?.role) && <th>Action</th>}</tr>
            </thead>
            <tbody>
              {reworks.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400"><RotateCcw size={32} className="mx-auto mb-2" />No rework items</td></tr>
              ) : reworks.map(rw => (
                <tr key={rw.id}>
                  <td><span className="font-mono text-xs font-semibold">{rw.dt_id?.slice(-8)}</span></td>
                  <td><span className="text-xs font-semibold text-slate-700">{rw.rework_type?.replace("_", " ")}</span></td>
                  <td className="text-xs text-slate-500 max-w-[200px] truncate">{rw.reject_reason || "—"}</td>
                  <td><span className={`text-[10px] font-bold uppercase ${rw.status === "approved" ? "text-green-600" : rw.status === "assigned" ? "text-orange-600" : "text-yellow-600"}`}>{rw.status}</span></td>
                  <td className="text-xs text-slate-600">{rw.circle_name}</td>
                  <td className="text-xs text-slate-500">{rw.assigned_date || "—"}</td>
                  {["admin", "ci", "supervisor"].includes(user?.role) && (
                    <td>
                      {rw.status !== "approved" && (
                        <button onClick={() => handleApproveRework(rw.id)} className="text-xs text-green-600 font-bold hover:text-green-700" data-testid={`approve-rework-${rw.id}`}>
                          Approve
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-sm p-6 w-full max-w-xl shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "Manrope" }}>Import QC Results</h3>
            <p className="text-xs text-slate-500 mb-4">Paste JSON array of QC records. Each record: dt_code, qc_level, result, reject_reason (optional), qc_date</p>
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <label className="field-label">JSON Data</label>
                <button onClick={() => setImportData(sampleJson)} className="text-xs text-orange-500 hover:underline">Load Sample</button>
              </div>
              <textarea
                className="field-input h-48 font-mono text-xs resize-none"
                placeholder={sampleJson}
                value={importData}
                onChange={e => setImportData(e.target.value)}
                data-testid="qc-import-textarea"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowImport(false)} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600">Cancel</button>
              <button onClick={handleBulkImport} disabled={importing || !importData} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60" data-testid="import-submit-btn">
                {importing ? "Importing..." : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
